import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { X, Lightning, MapPin } from "@phosphor-icons/react";
import AddressAutocomplete from "@/components/AddressAutocomplete";

/**
 * "Quick Job Here" — one-tap on-site job creator.
 * Creates a job assigned to caller, clocks them in, and snapshots hourly_rate.
 * Used by admins (self-bill) and workers (unplanned service call).
 *
 * Optional `initialAddress` / `initialUnit` pre-fill the form when the modal
 * is opened from a context that already knows where the job lives
 * (e.g. the Chew Flats client page auto-fills 5346 Chew Ave).
 */
export default function QuickJobModal({ open, onClose, initialAddress = "", initialUnit = "" }) {
  const nav = useNavigate();
  const [f, setF] = useState({ property_address: "", unit: "", title: "Service visit", priority: "normal", description: "" });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  // Reset + apply pre-fill each time the modal opens.
  useEffect(() => {
    if (open) {
      setF({
        property_address: initialAddress || "",
        unit: initialUnit || "",
        title: "Service visit",
        priority: "normal",
        description: "",
      });
    }
  }, [open, initialAddress, initialUnit]);

  if (!open) return null;

  const useMyLocation = async () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported on this device");
    const t = toast.loading("Getting your location…");
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { latitude, longitude } = pos.coords;
        const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18`);
        const d = await r.json();
        const addr = d.display_name || `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
        setF(prev => ({ ...prev, property_address: addr }));
        toast.success("Address filled", { id: t });
      } catch {
        toast.error("Couldn't get address — type it manually", { id: t });
      }
    }, () => toast.error("Location denied — type the address manually", { id: t }), { enableHighAccuracy: true, timeout: 8000 });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.property_address.trim()) return toast.error("Enter the address (or use GPS)");
    setBusy(true);
    const t = toast.loading("Starting job & clocking in…");
    try {
      const r = await api.post("/jobs/quick-start", {
        property_address: f.property_address.trim(),
        unit: f.unit.trim(),
        title: f.title.trim() || "Service visit",
        description: f.description.trim(),
        priority: f.priority,
      });
      toast.success(r.data.message || `Started ${r.data.work_order}`, { id: t });
      onClose();
      if (r.data.id) nav(`/admin/jobs/${r.data.id}`.replace("/admin/jobs", window.location.pathname.startsWith("/worker") ? "/worker/jobs" : "/admin/jobs"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't start job", { id: t });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70] p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-white border-2 border-[#0f172a] shadow-[8px_8px_0px_#0f172a]" onClick={e => e.stopPropagation()} data-testid="quick-job-modal">
        <div className="flex items-center justify-between p-4 border-b-2 border-[#0f172a] bg-gradient-to-r from-amber-400 to-amber-500 text-[#0f172a]">
          <div className="flex items-center gap-2">
            <Lightning weight="fill" size={22}/>
            <h3 className="font-display font-black text-xl uppercase tracking-wide">Quick Job Here</h3>
          </div>
          <button onClick={onClose} className="p-1"><X weight="bold" size={22}/></button>
        </div>
        <form onSubmit={submit} className="p-4 space-y-3" data-testid="quick-job-form">
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1">Address *</label>
            <AddressAutocomplete
              value={f.property_address}
              onChange={(v) => setF(prev => ({ ...prev, property_address: v }))}
              onSelect={({ label }) => setF(prev => ({ ...prev, property_address: label }))}
              placeholder="Start typing an address…"
              testId="quick-job-address"
              required
              rightSlot={(
                <button type="button" onClick={useMyLocation} data-testid="quick-job-gps-btn"
                  className="text-[10px] font-black uppercase tracking-widest text-[#00C4CC] hover:underline inline-flex items-center gap-1">
                  <MapPin weight="bold" size={11}/> Use My Location
                </button>
              )}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Unit</label>
              <input value={f.unit} onChange={upd("unit")} placeholder="A · 3B · 101"
                data-testid="quick-job-unit"
                className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm font-medium"/>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Priority</label>
              <select value={f.priority} onChange={upd("priority")} data-testid="quick-job-priority"
                className="w-full h-11 px-2 border-2 border-[#0f172a] text-sm font-bold uppercase bg-white">
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Title</label>
            <input value={f.title} onChange={upd("title")} data-testid="quick-job-title"
              className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm font-medium"/>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Notes (optional)</label>
            <textarea rows={2} value={f.description} onChange={upd("description")} data-testid="quick-job-desc"
              placeholder="What are you doing on-site?"
              className="w-full px-3 py-2 border-2 border-[#0f172a] text-sm font-medium resize-none"/>
          </div>
          <div className="text-[11px] font-medium text-slate-500 bg-slate-50 border-l-4 border-amber-500 px-2 py-1.5">
            ⚡ Creates job · assigns to you · clocks you in · snapshots your hourly rate for billing
          </div>
          <button type="submit" disabled={busy} data-testid="quick-job-submit"
            className="tactile-btn w-full h-12 bg-amber-500 hover:bg-amber-600 text-white font-black uppercase tracking-wider text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60">
            <Lightning weight="fill" size={16}/> {busy ? "Starting…" : "Start Job + Clock In"}
          </button>
        </form>
      </div>
    </div>
  );
}
